/**
 * Shared Accept-Language + CS/SK/EN pick for platform (non-ApiClient) envelopes.
 * Wire messages are `CS / SK / EN`; UIs must never throw the slash-joined string.
 */
import { pickLocalizedApiMessage } from 'pi-kiosk-shared';
import i18n from '../../i18n.js';

/** Supported customer email / OTP body locale (matches BE `cs|en|sk`). */
export type CustomerAuthLocale = 'cs' | 'en' | 'sk';

/** Active UI locale for platform fetch headers and envelope picks (default `cs`). */
export function customerPlatformUiLocale(): string {
  return i18n.resolvedLanguage ?? i18n.language ?? 'cs';
}

/**
 * Normalize i18n language (e.g. `en-US`, `sk`) to a BE auth/email locale code.
 * Used on OTP / register / forgot-password / verification request bodies.
 */
export function resolveCustomerAuthLocale(
  language: string | undefined,
): CustomerAuthLocale {
  if (language === undefined || language.length === 0) {
    return 'cs';
  }
  if (language.startsWith('en')) {
    return 'en';
  }
  if (language.startsWith('sk')) {
    return 'sk';
  }
  return 'cs';
}

/** Live UI language as `cs|en|sk` for customer-auth JSON bodies. */
export function customerAuthBodyLocale(): CustomerAuthLocale {
  return resolveCustomerAuthLocale(customerPlatformUiLocale());
}

/** Merge Accept + Accept-Language onto platform JSON fetch headers. */
export function platformJsonHeaders(
  extra: Record<string, string> = {},
): Record<string, string> {
  return {
    Accept: 'application/json',
    'Accept-Language': customerPlatformUiLocale(),
    ...extra,
  };
}

/** Pick one language from a platform `error` / `message` wire string. */
export function pickPlatformEnvelopeMessage(rawMessage: string): string {
  return pickLocalizedApiMessage(rawMessage, customerPlatformUiLocale());
}
